/**
 * The mic test's live "voice orb" — a canvas that shows, at a glance, who is
 * talking and how loudly, with the Spectra icon at its centre.
 *
 *   idle        a slow breathing orb
 *   connecting  a spinning arc
 *   listening   a ring of bars driven by YOUR microphone (success colour)
 *   thinking    orbiting dots while the agent works on its reply
 *   speaking    a ring of bars driven by the AI's audio (accent colour)
 *   error       a still, danger-tinted orb
 *
 * Levels come from real AnalyserNodes handed in through `analysers` (a ref,
 * so the parent can swap them without re-rendering this component). Drawing
 * runs in requestAnimationFrame and never touches React state, so a 60fps
 * animation costs no renders.
 */
import { useEffect, useRef } from 'react';

const SIZE = 240;          // CSS pixels; the canvas is scaled for devicePixelRatio
const BARS = 72;
const ORB_RADIUS = 54;
const ICON = '/favicon.png';

function cssVar(el, name, fallback) {
  const v = getComputedStyle(el).getPropertyValue(name).trim();
  return v || fallback;
}

/**
 * Any CSS colour (hex, rgb(), hsl(), a name) → a function giving it at an
 * alpha. Themes define their tokens in different notations; the canvas
 * normalises them for us when the string is assigned to fillStyle.
 */
function toRgba(g, color) {
  g.fillStyle = '#000';
  g.fillStyle = color;
  const c = g.fillStyle;
  let r = 0, gr = 0, b = 0;
  if (c.startsWith('#')) {
    r = parseInt(c.slice(1, 3), 16); gr = parseInt(c.slice(3, 5), 16); b = parseInt(c.slice(5, 7), 16);
  } else {
    [r, gr, b] = c.match(/\d+(\.\d+)?/g).map(Number);
  }
  return (alpha) => `rgba(${r},${gr},${b},${alpha})`;
}

/** 0..1 loudness from an analyser's time-domain signal (RMS, lightly boosted). */
function levelOf(analyser, buf) {
  if (!analyser) return 0;
  analyser.getByteTimeDomainData(buf);
  let sum = 0;
  for (let i = 0; i < buf.length; i++) {
    const s = (buf[i] - 128) / 128;
    sum += s * s;
  }
  return Math.min(1, Math.sqrt(sum / buf.length) * 3.2);
}

export default function VoiceOrb({ phase, analysers, muted = false }) {
  const canvasRef = useRef(null);
  const wellRef = useRef(null);
  const phaseRef = useRef(phase);
  const mutedRef = useRef(muted);
  // The draw loop reads these every frame without restarting.
  useEffect(() => {
    phaseRef.current = phase;
    mutedRef.current = muted;
  }, [phase, muted]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = SIZE * dpr;
    canvas.height = SIZE * dpr;
    const g = canvas.getContext('2d');
    g.scale(dpr, dpr);

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const freq = new Uint8Array(256);
    const wave = new Uint8Array(512);
    const bars = new Float32Array(BARS);
    let level = 0;
    let colors = null;
    let frame = 0;
    let raf = 0;

    const readColors = () => ({
      accent: toRgba(g, cssVar(canvas, '--accent-primary', '#8b5cf6')),
      user: toRgba(g, cssVar(canvas, '--success', '#10b981')),
      think: toRgba(g, cssVar(canvas, '--accent-primary-hover', '#a78bfa')),
      danger: toRgba(g, cssVar(canvas, '--danger', '#ef4444')),
      idle: toRgba(g, cssVar(canvas, '--text-tertiary', '#6b7280')),
    });

    const draw = (now) => {
      raf = requestAnimationFrame(draw);
      // Re-read theme colours now and then, so a theme switch mid-call follows.
      if (!colors || frame++ % 90 === 0) colors = readColors();

      const p = phaseRef.current;
      const t = now / 1000;
      const cx = SIZE / 2;
      const cy = SIZE / 2;
      const a = analysers?.current || {};
      const source = p === 'speaking' ? a.ai : (p === 'listening' && !mutedRef.current ? a.mic : null);
      const color = p === 'speaking' ? colors.accent
        : p === 'listening' ? colors.user
        : p === 'thinking' ? colors.think
        : p === 'error' ? colors.danger
        : p === 'connecting' ? colors.accent
        : colors.idle;

      // Loudness drives the orb size; ease toward the target so it never jitters.
      const target = source ? levelOf(source, wave) : 0;
      level += (target - level) * (target > level ? 0.45 : 0.12);

      g.clearRect(0, 0, SIZE, SIZE);

      // Soft halo behind everything.
      const haloR = ORB_RADIUS * (1.6 + level * 0.9);
      const halo = g.createRadialGradient(cx, cy, ORB_RADIUS * 0.6, cx, cy, haloR);
      halo.addColorStop(0, color(0.33));
      halo.addColorStop(1, color(0));
      g.fillStyle = halo;
      g.beginPath();
      g.arc(cx, cy, haloR, 0, Math.PI * 2);
      g.fill();

      // Frequency ring — only while someone is actually producing sound.
      if (source) {
        source.getByteFrequencyData(freq);
        // Speech energy sits in the low bins; spread them over half the ring
        // and mirror, so the shape is symmetric.
        const usable = Math.floor(source.frequencyBinCount * 0.45);
        for (let i = 0; i < BARS; i++) {
          const half = i < BARS / 2 ? i : BARS - 1 - i;
          const bin = Math.floor((half / (BARS / 2)) * usable);
          const v = (freq[bin] || 0) / 255;
          bars[i] += (v - bars[i]) * 0.35;
        }
      } else {
        for (let i = 0; i < BARS; i++) bars[i] *= 0.85;
      }
      const ringR = ORB_RADIUS * (1 + level * 0.28) + 10;
      g.lineCap = 'round';
      g.lineWidth = 3;
      for (let i = 0; i < BARS; i++) {
        const len = bars[i] * 34;
        if (len < 0.6) continue;
        const ang = (i / BARS) * Math.PI * 2 - Math.PI / 2;
        g.strokeStyle = color(0.35 + bars[i] * 0.65);
        g.beginPath();
        g.moveTo(cx + Math.cos(ang) * ringR, cy + Math.sin(ang) * ringR);
        g.lineTo(cx + Math.cos(ang) * (ringR + len), cy + Math.sin(ang) * (ringR + len));
        g.stroke();
      }

      // The orb itself.
      const breathe = p === 'idle' && !reduceMotion ? Math.sin(t * 1.6) * 2.5 : 0;
      const r = ORB_RADIUS * (1 + level * 0.28) + breathe;
      const body = g.createRadialGradient(cx - r * 0.35, cy - r * 0.4, r * 0.1, cx, cy, r);
      body.addColorStop(0, color(1));
      body.addColorStop(0.65, color(0.8));
      body.addColorStop(1, color(0.55));
      g.fillStyle = body;
      g.beginPath();
      g.arc(cx, cy, r, 0, Math.PI * 2);
      g.fill();

      // Gloss highlight — keeps the orb from reading as a flat circle.
      const gloss = g.createRadialGradient(cx - r * 0.3, cy - r * 0.45, 0, cx - r * 0.3, cy - r * 0.45, r * 0.7);
      gloss.addColorStop(0, 'rgba(255,255,255,0.45)');
      gloss.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = gloss;
      g.beginPath();
      g.arc(cx, cy, r, 0, Math.PI * 2);
      g.fill();

      if (p === 'connecting') {
        const start = reduceMotion ? 0 : t * 4;
        g.strokeStyle = color(1);
        g.lineWidth = 3;
        g.beginPath();
        g.arc(cx, cy, r + 14, start, start + Math.PI * 0.6);
        g.stroke();
      }

      if (p === 'thinking') {
        // Three dots chasing each other round the orb.
        for (let k = 0; k < 3; k++) {
          const ang = (reduceMotion ? 0 : t * 2.6) + k * 0.55 - Math.PI / 2;
          const dr = r + 16;
          g.fillStyle = color([1, 0.7, 0.45][k]);
          g.beginPath();
          g.arc(cx + Math.cos(ang) * dr, cy + Math.sin(ang) * dr, 4.5 - k, 0, Math.PI * 2);
          g.fill();
        }
      }

      // The icon well breathes with the orb, so the mark moves with the voice.
      if (wellRef.current) {
        wellRef.current.style.transform = `translate(-50%, -50%) scale(${(1 + level * 0.12).toFixed(3)})`;
      }
    };

    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [analysers]);

  return (
    <div className="mt-orb-wrap" style={{ width: SIZE, height: SIZE }}>
      <canvas ref={canvasRef} className="mt-orb-canvas" style={{ width: SIZE, height: SIZE }} aria-hidden="true" />
      <div ref={wellRef} className={`mt-orb-well ${phase === 'idle' || phase === 'error' ? 'dim' : ''}`}>
        <img src={ICON} alt="Spectra" />
      </div>
    </div>
  );
}
